import { KIT_SLOTS, type KitSlotGroup, type KitSlotInfo } from 'fym-shared/src/music24/assetKinds';
import {
  groupSlotChannels,
  oneOffChannelsOfTheme,
  publishOfAssetType,
  type OneOffChannel,
  type SerializedAssetPublish,
  type SlotChannelGroup,
} from 'fym-shared/src/music24/oneOffPublish';
import { needsFullVideoPlan, runCredits, STUDIO_THEMES, type StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type {
  CatalogItem,
  CreateKitAssetItem,
  GetReleaseKitResponse,
  SerializedKitAsset,
} from '../../../../../redux/api/fymAPI.types';
import { buildRevisionCards, type RevisionCard } from '../assets/revisionCards';
import { buildStudioItems, themeState, type StudioSongContext } from '../../studio/studioRules';
import { isWaitingSlotMark } from '../../publish/publishBadgeText';
import { sourceOfAsset } from '../../addedAssets/addedAssetFacts';

/**
 * The slots of the Kit tab (design assets-rethink b1): one slot per keeper asset type of fym-shared
 * `KIT_SLOTS`, plus the cover. A slot is ONE asset type: the three banners, the motion artwork pair,
 * the cover sizes and the thumbnails of each video are one slot. Pure, so it is tested without React.
 *
 * The state of a slot (one word, design b1 `.st`):
 * - `made`: a ready file exists (also while a newer revision is made or failed: the ready one stays).
 * - `making`: no ready file, a revision is queued or made now.
 * - `failed`: no ready file, the newest revision failed (its credits came back).
 * - `notMade`: nothing yet.
 * - `locked`: nothing yet and the plan cannot make it (full videos and the motion artwork, d3).
 * The counts "N of M made" use the same rule as the tab figure (`releaseTabFigures`).
 */
export type KitSlotState = 'made' | 'making' | 'failed' | 'notMade' | 'locked';

export interface KitSlotView {
  slot: KitSlotInfo;
  state: KitSlotState;
  /** The kit cards of the slot (one per output variant). Empty for the cover and a slot not made. */
  cards: RevisionCard[];
  /** The ready revision that the slot shows and opens (the first output with a ready file). */
  readyAsset: SerializedKitAsset | null;
  /** The ready file of each output, in the order of the cards ("Download 3"). */
  readyAssets: SerializedKitAsset[];
  /** The card of `readyAsset`. */
  readyCard: RevisionCard | null;
  /** A newer revision that is made now (also over a ready one). */
  workingAsset: SerializedKitAsset | null;
  /** The newest revision that failed (also over a ready one: "r2 failed. r1 stays."). */
  failedAsset: SerializedKitAsset | null;
  /** 0..100 of the working revision. */
  progress: number;
  /** The credits of one run of the asset type with its default files. */
  credits: number;
  /** A ready file of the slot is a preview render (Free plan, the trial). */
  preview: boolean;
}

export interface KitSlotInput {
  item: Pick<CatalogItem, 'artworkUrl' | 'artworkFile'>;
  release: Pick<GetReleaseKitResponse, 'assets' | 'newest'>;
  /** False on Free and Artist: the full videos and the motion artwork are locked when not made. */
  canRenderFullSongVideos: boolean;
}

function readyOf(card: RevisionCard): SerializedKitAsset | null {
  return card.asset?.status === 'ready' ? card.asset : card.readyAsset;
}

/** The thumbnail of the audio upload (`youtube`) and the square motion artwork lead their slot. */
const LEAD_VARIANTS: ReadonlyArray<string> = ['youtube', 'square'];

function sortCards(cards: RevisionCard[]): RevisionCard[] {
  return [...cards].sort((a, b) => {
    const lead = Number(LEAD_VARIANTS.includes(b.variant)) - Number(LEAD_VARIANTS.includes(a.variant));
    return lead !== 0 ? lead : a.variant.localeCompare(b.variant);
  });
}

function themeSlot(
  slot: KitSlotInfo,
  themeId: StudioThemeId,
  all: RevisionCard[],
  canRenderFull: boolean
): KitSlotView {
  const cards = sortCards(all.filter((card) => card.kind === 'kitAsset' && card.themeId === themeId));
  const readyCard = cards.find((card) => readyOf(card) !== null) ?? null;
  const readyAssets = cards.map(readyOf).filter((asset): asset is SerializedKitAsset => asset !== null);
  const working = cards.find((card) => card.state === 'working' || card.state === 'queued') ?? null;
  const failed = cards.find((card) => card.state === 'failed') ?? null;
  let state: KitSlotState;
  if (readyCard !== null) {
    state = 'made';
  } else if (working !== null) {
    state = 'making';
  } else if (failed !== null) {
    state = 'failed';
  } else {
    // Every plan opens the music video editor and adds its own video (spec 2026-10-09 D7): its slot
    // is never locked. Only "Make the video" in the editor needs Pro.
    state = needsFullVideoPlan(themeId) && !canRenderFull && themeId !== 'musicVideo' ? 'locked' : 'notMade';
  }
  return {
    slot,
    state,
    cards,
    readyAsset: readyCard === null ? null : readyOf(readyCard),
    readyAssets,
    readyCard,
    workingAsset: working?.asset ?? null,
    failedAsset: failed?.asset ?? null,
    progress: working?.progress ?? 0,
    credits: runCredits(themeId, STUDIO_THEMES[themeId].outputs),
    preview: readyAssets.some((asset) => asset.preview === true),
  };
}

function coverSlot(slot: KitSlotInfo, item: KitSlotInput['item']): KitSlotView {
  const hasCover = item.artworkUrl !== null || item.artworkFile !== null;
  return {
    slot,
    state: hasCover ? 'made' : 'notMade',
    cards: [],
    readyAsset: null,
    readyAssets: [],
    readyCard: null,
    workingAsset: null,
    failedAsset: null,
    progress: 0,
    credits: 0,
    preview: false,
  };
}

/** Every slot of the Kit tab, in page order. */
export function buildKitSlots(input: KitSlotInput): KitSlotView[] {
  const cards = buildRevisionCards({ assets: input.release.assets, newest: input.release.newest, reels: [] });
  return KIT_SLOTS.map((slot) =>
    slot.themeId === null
      ? coverSlot(slot, input.item)
      : themeSlot(slot, slot.themeId, cards, input.canRenderFullSongVideos)
  );
}

export function slotsOfGroup(slots: KitSlotView[], group: KitSlotGroup): KitSlotView[] {
  return slots.filter((one) => one.slot.group === group);
}

/** One channel of one ready file of a slot, with the record that the file has there. */
export interface SlotChannel {
  channel: OneOffChannel;
  record: SerializedAssetPublish | null;
  asset: SerializedKitAsset;
}

/** The one-off channel records of a slot: one per channel of each ready output. */
export function slotPublishes(
  view: KitSlotView,
  publishes: readonly SerializedAssetPublish[],
  catalogItemUuid: string
): SlotChannel[] {
  return view.readyAssets.flatMap((asset) =>
    oneOffChannelsOfTheme(asset.themeId, asset.variant).map((channel) => ({
      channel,
      asset,
      record: publishOfAssetType(
        publishes,
        { catalogItemUuid, themeId: asset.themeId, variant: asset.variant },
        channel
      ),
    }))
  );
}

/**
 * The badges of a slot: ONE group per channel (owner fix 2026-10-10: the four YouTube thumbnails
 * showed "Not set on YouTube" four times). A linked YouTube video is on YouTube already and has its
 * own badge, so its file takes no channel here.
 */
export function slotChannelGroups(
  view: KitSlotView,
  publishes: readonly SerializedAssetPublish[],
  catalogItemUuid: string
): Array<SlotChannelGroup<SlotChannel>> {
  return groupSlotChannels(
    slotPublishes(view, publishes, catalogItemUuid).filter((entry) => sourceOfAsset(entry.asset) !== 'youtube')
  );
}

export interface KitSummary {
  total: number;
  made: number;
  making: number;
  failed: number;
  /** Not made and locked slots. */
  notMade: number;
  /**
   * The amber manual badges only (a made Canvas not marked on Spotify, a made motion artwork not
   * marked on Apple Music). The design b1 counted the banners too; they are dashed, not amber.
   */
  waitForYou: number;
}

export function summarizeKitSlots(
  slots: KitSlotView[],
  publishes: readonly SerializedAssetPublish[],
  catalogItemUuid: string
): KitSummary {
  const count = (state: KitSlotState): number => slots.filter((one) => one.state === state).length;
  // One per amber badge: a slot with two motion artwork files counts once.
  const waitForYou = slots
    .filter((one) => one.state === 'made')
    .flatMap((one) => slotChannelGroups(one, publishes, catalogItemUuid))
    .filter(isWaitingSlotMark).length;
  return {
    total: slots.length,
    made: count('made'),
    making: count('making'),
    failed: count('failed'),
    notMade: count('notMade') + count('locked'),
    waitForYou,
  };
}

/** The song facts that "Make it" and "Make the N missing ones" need with no step 3. */
export type KitSongContext = StudioSongContext & { hookStartSec: number | null; durationSec?: number | null };

/**
 * The items of one slot with the defaults of the Studio. Null for the cover and for a type that the
 * song cannot make now (no master, no lyrics, the plan, the clip bank): its "Make it" opens the
 * Studio, which says why.
 */
export function slotItems(view: KitSlotView, song: KitSongContext): CreateKitAssetItem[] | null {
  const { themeId } = view.slot;
  if (themeId === null || themeState(STUDIO_THEMES[themeId], song) !== 'open') {
    return null;
  }
  return buildStudioItems(themeId, {}, song);
}

/**
 * "Make the N missing ones" (design b1): the not-made and failed slots that a click can make with
 * the defaults (not the cover, not a locked slot), as one batch. At most 20 items (the route limit).
 */
export function missingKitItems(slots: KitSlotView[], song: KitSongContext): CreateKitAssetItem[] {
  return slots
    .filter((one) => one.state === 'notMade' || one.state === 'failed')
    .flatMap((one) => slotItems(one, song) ?? [])
    .slice(0, 20);
}

/** The slots that "Make the N missing ones" makes. */
export function missingSlotCount(slots: KitSlotView[], song: KitSongContext): number {
  return slots.filter(
    (one) => (one.state === 'notMade' || one.state === 'failed') && (slotItems(one, song)?.length ?? 0) > 0
  ).length;
}

/**
 * Why a not-made slot is not in "Make the N missing ones" (QA 2026-10-28, imp-8, lit-8, mob-13:
 * "5 not made" next to "Make the 3 missing ones" read as a lost item):
 * - `master`: the type reads the song file, and the song has no ready master;
 * - `lyrics`: the type needs the lyrics;
 * - `releaseDay`: the countdowns need a release day in the future;
 * - `plan`: the plan cannot make it (a locked slot);
 * - `editor`: the music video is made in its editor;
 * - `cover`: the song has no cover;
 * - `choice`: the type needs a choice in the Studio.
 */
export type KitMissingBlock = 'master' | 'lyrics' | 'releaseDay' | 'plan' | 'editor' | 'cover' | 'choice';

/** The order of the reasons in the line under the button. */
export const KIT_MISSING_BLOCKS: readonly KitMissingBlock[] = [
  'master',
  'lyrics',
  'releaseDay',
  'plan',
  'editor',
  'cover',
  'choice',
];

function blockOfSlot(view: KitSlotView, song: KitSongContext): KitMissingBlock | null {
  const { themeId } = view.slot;
  if (view.state === 'locked') {
    return 'plan';
  }
  if (themeId === null) {
    return 'cover';
  }
  if ((slotItems(view, song)?.length ?? 0) > 0) {
    return null;
  }
  if (themeId === 'musicVideo') {
    return 'editor';
  }
  switch (themeState(STUDIO_THEMES[themeId], song)) {
    case 'needsMaster':
      return 'master';
    case 'needsLyrics':
      return 'lyrics';
    case 'needsReleaseDate':
      return 'releaseDay';
    case 'needsPlan':
      return 'plan';
    case 'open':
      return 'choice';
  }
}

/**
 * The not-made slots that "Make the N missing ones" leaves out, by reason. With the slots that it
 * makes (`missingSlotCount`) they add up to the "not made" and "failed" chips.
 */
export function missingKitBlocks(slots: KitSlotView[], song: KitSongContext): Map<KitMissingBlock, number> {
  const blocks = new Map<KitMissingBlock, number>();
  slots
    .filter((one) => one.state === 'notMade' || one.state === 'failed' || one.state === 'locked')
    .forEach((one) => {
      const block = blockOfSlot(one, song);
      if (block !== null) {
        blocks.set(block, (blocks.get(block) ?? 0) + 1);
      }
    });
  return blocks;
}

/**
 * The note under the grid (design b4): a long video whose newest ready revision is newer than the
 * revision that went to YouTube. A badge belongs to the revision that went out.
 */
export interface StaleChannelNote {
  themeId: StudioThemeId;
  channel: OneOffChannel;
  current: number;
  published: number;
}

export function staleChannelNotes(
  slots: KitSlotView[],
  publishes: readonly SerializedAssetPublish[],
  catalogItemUuid: string
): StaleChannelNote[] {
  return slots.flatMap((one) =>
    one.slot.group !== 'longVideos' || one.readyAsset === null
      ? []
      : slotPublishes(one, publishes, catalogItemUuid)
          .filter(
            (entry) =>
              entry.channel === 'youtube' &&
              entry.record?.status === 'published' &&
              entry.record.revision < entry.asset.version
          )
          .map((entry) => ({
            themeId: entry.asset.themeId,
            channel: entry.channel,
            current: entry.asset.version,
            published: entry.record?.revision ?? 0,
          }))
  );
}

/** True when the Kit has nothing made at all (design d2: "Nothing made for <song> yet"). */
export function isKitEmpty(slots: KitSlotView[]): boolean {
  return slots.every((one) => one.slot.themeId === null || one.state === 'notMade' || one.state === 'locked');
}
